<template>
  <div>
    <v-container class="content-wrapper" id="create-news-events">
      <v-row>
        <v-col xl="3" lg="3" md="4" sm="4" xs="12">
          <h2 class="black--text font-weight-bold">
            {{ $t('create-news-and-events') }}
          </h2>
        </v-col>
        <v-col cols="12" md="8" xl="9" lg="9" sm="8" xs="12" class="d-flex justify-end flex-wrap">
          <v-btn outlined class="custom-button" rounded large @click="cancel">
            <v-icon class="mr-2">mdi-close-box-outline</v-icon>
            {{ $t('cancel') }}
          </v-btn>
          <v-btn outlined class="custom-button" rounded large @click="togglePreview">
            <v-icon class="mr-2">mdi-eye-outline</v-icon>
            {{ $t('preview') }}
          </v-btn>
          <v-btn outlined class="custom-button" rounded large @click="saveAsDraft">
            <v-icon class="mr-2">mdi-note-text-outline</v-icon>
            {{ $t('save-as-draft') }}
          </v-btn>
          <v-btn outlined class="custom-button" rounded large @click="publish">
            <v-icon class="mr-2">mdi-upload</v-icon>
            {{ $t('publish') }}
          </v-btn>
        </v-col>
      </v-row>

      <v-divider class="my-5"></v-divider>

      <v-row>
        <!-- Left Side Body -->
        <v-col xl="3" lg="3" md="4" sm="4" xs="12">
          <v-form ref="lestSideForm">
            <v-card elevation="3" style="border-radius: 24px;">
              <v-card-text class="mt-2 mb-0 pb-0">
                <v-file-input
                  v-model="newsAndEvents.thumbnail"
                  outlined
                  accept="image/*"
                  :placeholder="'Upload thumbnail'"
                  :clearable="false"
                  dense
                  prepend-icon=""
                  prepend-inner-icon="mdi-upload"
                  class="required"
                  :rules="requiredRule()"
                >
                </v-file-input>
              </v-card-text>

              <v-divider class="mx-5"></v-divider>

              <v-card-text class="my-0 py-0">
                <v-radio-group v-model="newsAndEvents.type" mandatory>
                  <v-radio label="Event" :value="'EVENT'"></v-radio>
                  <v-radio label="News" :value="'NEWS'"></v-radio>
                </v-radio-group>
              </v-card-text>

              <v-divider class="mx-5"></v-divider>

              <v-card-text class="mb-0 pb-0">
                <label>Scientific Domain</label>
                <v-autocomplete
                  v-model="newsAndEvents.domains"
                  class="mt-2 "
                  :items="domains"
                  :item-value="'id'"
                  :item-text="'name'"
                  outlined
                  multiple
                  clearable
                  :placeholder="'Select'"
                  dense
                  :rules="requiredRuleList()"
                >
                </v-autocomplete>
              </v-card-text>
              <v-card-text class="my-0 py-0">
                <label>Subdomain</label>
                <v-autocomplete
                  v-model="newsAndEvents.subDomains"
                  class="mt-2"
                  :items="filteredSubDomains"
                  :item-value="'id'"
                  :item-text="'subDomain'"
                  outlined
                  multiple
                  dense
                  :rules="requiredRuleList()"
                  :placeholder="'Select'"
                  clearable
                ></v-autocomplete>
              </v-card-text>

              <v-divider class="mx-5"></v-divider>

              <v-card-text v-if="newsAndEvents.type === 'NEWS'">
                <v-menu
                  v-model="newsAndEvents.dateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      outlined
                      v-model="newsAndEvents.date"
                      label="Date"
                      clearable
                      append-icon="mdi-calendar"
                      readonly
                      dense
                      v-bind="attrs"
                      v-on="on"
                      class="required"
                      :rules="requiredRule()"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="newsAndEvents.date"
                    @input="newsAndEvents.dateMenu = false"
                    color="rgba(157, 87, 244, 1)"
                    :first-day-of-week="1"
                  ></v-date-picker>
                </v-menu>
              </v-card-text>
              <v-card-text v-if="newsAndEvents.type === 'EVENT'">
                <v-menu
                  v-model="newsAndEvents.dateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      outlined
                      v-model="fillingDateRangeText"
                      label="Date"
                      clearable
                      append-icon="mdi-calendar"
                      readonly
                      dense
                      v-bind="attrs"
                      v-on="on"
                      class="required"
                      :rules="requiredRule()"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="newsAndEvents.eventDate"
                    @change="validateDateRange"
                    range
                    color="rgba(157, 87, 244, 1)"
                    :first-day-of-week="1"
                  ></v-date-picker>
                </v-menu>
              </v-card-text>
            </v-card>
          </v-form>
        </v-col>

        <!-- Main Body -->
        <v-col cols="12" md="8" xl="9" lg="9" sm="8" xs="12">
          <v-card elevation="3" style="border-radius: 24px;">
            <v-form ref="mainBodyForm">
              <v-card-text class="mt-2 mb-0 pb-0">
                <v-text-field
                  v-model="newsAndEvents.title"
                  class="required"
                  :rules="requiredRule()"
                  label="Title"
                  outlined
                ></v-text-field>
              </v-card-text>
              <v-card-text class="mt-0 pt-0">
                <tiptap-vuetify
                  v-model="newsAndEvents.description"
                  :placeholder="'Description *'"
                  :extensions="extensions"
                  class="required custom-tiptap"
                  :rules="requiredRule()"
                ></tiptap-vuetify>
              </v-card-text>
              <v-card-title>
                <div class="black--text font-weight-bold">
                  Tags
                </div>
              </v-card-title>
              <v-card-text>
                <div class="grey--text font-weight-bold">
                  Enter your tags (keywords) by pressing enter.
                </div>
              </v-card-text>
              <v-card-text>
                <v-text-field
                  v-model="newsAndEvents.tag"
                  label="Type Tag Names"
                  outlined
                  @keyup.enter="addTag"
                ></v-text-field>
                <v-chip-group column>
                  <v-chip
                    v-for="(tag, index) in tags"
                    :key="index"
                    :color="color(index)"
                    close
                    @click:close="removeTag(index)"
                  >
                    {{ tag }}
                  </v-chip>
                </v-chip-group>
              </v-card-text>
              <v-card-text>
                <v-file-input label="Attach Files" outlined v-model="selectedFile"></v-file-input>
              </v-card-text>
              <v-card-text v-if="files.length > 0">
                <v-chip
                  v-for="(file, index) in files"
                  :key="index"
                  :color="color(index)"
                  close
                  @click:close="removeFile(index)"
                  class="ma-1"
                >
                  {{ file.name }}
                </v-chip>
              </v-card-text>
              <v-card-text v-if="filesFromResponse.length > 0">
                <v-chip
                  v-for="(file, index) in filesFromResponse"
                  :key="index"
                  :color="color(index)"
                  close
                  @click:close="removefileFromResponse(index)"
                  class="ma-1"
                >
                  {{ file }}
                </v-chip>
              </v-card-text>
              <v-card-actions class="pt-0">
                <v-row style="width: 100%;" justify="center">
                  <v-col cols="6">
                    <v-btn
                      block
                      depressed
                      rounded
                      large
                      class="custom-button"
                      outlined
                      @click="addFile"
                      >Add File
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-col>
      </v-row>

      <!-- Dialog Preview -->
      <v-dialog v-model="previewDialog" max-width="75%">
        <v-card
          class="py-2 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8"
        >
          <v-card-text>
            <v-container>
              <v-row class="mt-0 pt-0 text-center">
                <v-spacer></v-spacer>
                <v-col cols="12" xl="10" lg="10" md="12" sm="12">
                  <h2>
                    Preview
                    {{ dialogType.charAt(0).toUpperCase() + dialogType.slice(1).toLowerCase() }}
                  </h2>
                </v-col>
                <v-spacer></v-spacer>
              </v-row>
              <v-row class="mt-0 pt-0">
                <v-spacer></v-spacer>
                <v-col cols="12" xl="10" lg="10" md="12" sm="12">
                  <h2 class="primary300--text">
                    {{ dialogTitle }}
                  </h2>
                </v-col>
                <v-spacer></v-spacer>
              </v-row>
              <v-row v-if="newsAndEvents.type === 'NEWS'" class="mt-0 pt-0">
                <v-spacer></v-spacer>
                <v-col cols="12" xl="10" lg="10" md="12" sm="12">
                  <p class="black--text font-weight-bold">
                    {{ formatDateLocaleAE(dialogDate) }}
                  </p>
                </v-col>
                <v-spacer></v-spacer>
              </v-row>
              <v-row v-if="newsAndEvents.type === 'EVENT'" class="mt-0 pt-0">
                <v-spacer></v-spacer>
                <v-col cols="12" xl="10" lg="10" md="12" sm="12">
                  <p v-if="newsAndEvents.eventDate.length > 1" class="black--text font-weight-bold">
                    {{ formatDate(newsAndEvents.eventDate[0]) }} -
                    {{ formatDate(newsAndEvents.eventDate[1]) }}
                  </p>
                  <p v-else class="black--text font-weight-bold">
                    {{ formatDate(newsAndEvents.eventDate[0]) }}
                  </p>
                </v-col>
                <v-spacer></v-spacer>
              </v-row>
              <v-row class="mt-0 pt-0">
                <v-spacer></v-spacer>
                <v-col cols="12" xl="6" lg="6" md="12" sm="12" v-if="dialogType === 'NEWS'">
                  <v-img
                    v-if="thumbnail"
                    :src="thumbnail"
                    style="border-radius: 24px; overflow: hidden;"
                  >
                  </v-img>
                  <v-img
                    v-else
                    width="100%"
                    height="477px"
                    :src="require('@/assets/img/landing-page/pages/news.png')"
                    gradient="to top right, rgba(61, 1, 154, 0.4), rgba(61, 1, 154, 0.4)"
                    style="border-radius: 24px; overflow: hidden;"
                  ></v-img>
                </v-col>
                <v-col cols="12" xl="6" lg="6" md="12" sm="12" v-if="dialogType === 'EVENT'">
                  <v-img
                    v-if="thumbnail"
                    :src="thumbnail"
                    style="border-radius: 24px; overflow: hidden;"
                  >
                  </v-img>
                  <v-img
                    v-else
                    width="100%"
                    height="477px"
                    :src="require('@/assets/img/landing-page/pages/events.png')"
                    gradient="to top right, rgba(61, 1, 154, 0.4), rgba(61, 1, 154, 0.4)"
                    style="border-radius: 24px; overflow: hidden;"
                  ></v-img>
                </v-col>
                <v-spacer></v-spacer>
              </v-row>
              <v-row class="mt-5 pt-5">
                <v-spacer></v-spacer>
                <v-col cols="12" xl="6" lg="6" md="12" sm="12">
                  <div v-html="dialogDescription"></div>
                </v-col>
                <v-spacer></v-spacer>
              </v-row>
              <v-row v-if="tags.length > 0" class="mt-0 pt-0">
                <v-spacer></v-spacer>
                <v-col cols="12" xl="6" lg="6" md="12" sm="12">
                  <h3 class="primary200--text">
                    Tags
                  </h3>
                </v-col>
                <v-spacer></v-spacer>
              </v-row>
              <v-row v-if="tags.length > 0" class="mt-0 pt-0">
                <v-spacer></v-spacer>
                <v-col cols="12" xl="6" lg="6" md="12" sm="12">
                  <v-chip
                    :color="color(index)"
                    v-for="(tag, index) in tags"
                    :key="index"
                    class="ma-1"
                  >
                    {{ tag }}
                  </v-chip>
                </v-col>
                <v-spacer></v-spacer>
              </v-row>
              <v-row v-if="files.length > 0" class="mt-5 pt-5">
                <v-spacer></v-spacer>
                <v-col cols="12" xl="6" lg="6" md="12" sm="12">
                  <h3 class="primary200--text">
                    Files
                  </h3>
                </v-col>
                <v-spacer></v-spacer>
              </v-row>
              <v-row v-if="files.length > 0" class="mt-0 pt-0">
                <v-spacer></v-spacer>
                <v-col cols="12" xl="6" lg="6" md="12" sm="12">
                  <v-chip
                    v-for="(file, index) in files"
                    :key="index"
                    :color="color(index)"
                    class="ma-1"
                  >
                    {{ file.name }}
                  </v-chip>
                </v-col>
                <v-spacer></v-spacer>
              </v-row>
              <v-row v-if="filesFromResponse.length > 0" class="mt-0 pt-0">
                <v-spacer></v-spacer>
                <v-col cols="12" xl="6" lg="6" md="12" sm="12">
                  <v-chip
                    v-for="(file, index) in filesFromResponse"
                    :key="index"
                    :color="color(index)"
                    class="ma-1"
                  >
                    {{ file }}
                  </v-chip>
                </v-col>
                <v-spacer></v-spacer>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import util from '@/mixins/util';
import { Bold, Underline, Italic, Strike, TiptapVuetify, Link } from 'tiptap-vuetify';
import EventBus from '@/helpers/event-bus.js';

export default {
  name: 'CreateNewsAndEvents',
  components: { TiptapVuetify },
  mixins: [util],
  data: () => ({
    newsAndEvents: {
      type: 'EVENT',
      domains: [],
      subDomains: [],
      thumbnail: null,
      date: null,
      eventDate: [],
      dateMenu: false,
      title: '',
      tag: '',
      description: '',
    },
    tags: [],
    domains: [],
    subDomains: [],
    extensions: [Bold, Underline, Italic, Strike, Link],
    previewDialog: false,
    dialogDescription: '',
    dialogTitle: '',
    dialogDate: null,
    dialogType: '',
    dialogThumbnail: null,
    editNewsAndEvents: '',
    selectedFile: null,
    files: [],
    filesFromResponse: [],
  }),
  watch: {
    'newsAndEvents.thumbnail'(newVal, oldVal) {
      if (oldVal && newVal && this.editNewsAndEvents != null) {
        console.log('Thumbnail changed from:', oldVal, 'to:', newVal);

        if (newVal instanceof File) {
          this.dialogThumbnail = URL.createObjectURL(newVal);
        } else {
          this.dialogThumbnail = newVal;
        }
      }
    },
    'newsAndEvents.domains': {
      handler(newDomains, oldDomains) {
        const removedDomains = oldDomains.filter(domain => !newDomains.includes(domain));

        if (removedDomains.length > 0) {
          this.newsAndEvents.subDomains = this.newsAndEvents.subDomains.filter(subDomainId => {
            const subDomain = this.subDomains.find(sub => sub.id === subDomainId);
            return subDomain && newDomains.includes(subDomain.domain);
          });
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.axios.get('/api/v1/scientific-domains').then(res => {
      this.domains = res.data.content;
    });
    this.axios.get('/api/v1/scientific-subdomains').then(res => {
      this.subDomains = res.data.content;
    });

    this.editNewsAndEvents = this.$route.query.edit;
    if (this.editNewsAndEvents) {
      this.getNewsAndEvents();
    }
  },
  computed: {
    filteredSubDomains() {
      if (!this.newsAndEvents.domains.length) return [];
      return this.subDomains.filter(sub => this.newsAndEvents.domains.includes(sub.domain));
    },
    scientificsIds() {
      return this.newsAndEvents.subDomains.map(subDomain => {
        const matchingSubDomain = this.subDomains.find(sub => sub.id === subDomain.id);

        return matchingSubDomain ? matchingSubDomain.id : subDomain;
      });
    },
    thumbnail() {
      if (this.dialogThumbnail) {
        return this.dialogThumbnail;
      } else if (this.newsAndEvents.thumbnail instanceof File) {
        return URL.createObjectURL(this.newsAndEvents.thumbnail);
      }

      return this.newsAndEvents.thumbnail;
    },
    fillingDateRangeText() {
      return this.newsAndEvents.eventDate.join(' ~ ');
    },
  },
  methods: {
    saveAsDraft() {
      const isLeftSideFormValid = this.$refs.lestSideForm.validate();
      const isMainSideFormValid = this.$refs.mainBodyForm.validate();

      if (!this.newsAndEvents.description) {
        EventBus.$emit('snackbar', {
          text: 'Please provide a description to save as a draft.',
          color: 'error',
        });
        return;
      }

      let dateFrom = null;
      let dateTo = null;
      let date = null;

      if (this.newsAndEvents.eventDate && this.newsAndEvents.eventDate.length > 0) {
        if (this.newsAndEvents.eventDate.length === 1) {
          date = this.newsAndEvents.eventDate[0];
          dateFrom = '';
          dateTo = '';
        } else if (this.newsAndEvents.eventDate.length === 2) {
          dateFrom = this.newsAndEvents.eventDate[0];
          dateTo = this.newsAndEvents.eventDate[1];
          date = '';
        }
      }

      if (isLeftSideFormValid && isMainSideFormValid) {
        EventBus.$emit('waiting', true);
        const payload = {
          title: this.newsAndEvents.title,
          description: this.newsAndEvents.description,
          tags: this.tags,
          type: this.newsAndEvents.type,
          status: 'DRAFT',
          scientificsIds: this.scientificsIds,
          ...(this.editNewsAndEvents && { id: this.$route.params.id }),

          ...(this.newsAndEvents.type === 'NEWS' && { date: this.newsAndEvents.date }),

          ...(this.newsAndEvents.type === 'EVENT' &&
            (date ? { date } : { ...(dateFrom && { dateFrom }), ...(dateTo && { dateTo }) })),
        };

        console.log('test', payload);

        this.axios
          .post(`/api/v1/news-and-events`, payload)
          .then(response => {
            console.log('Saved as draft Successfully:', response.data);
            const id = response.data.id;

            EventBus.$emit('snackbar', {
              text: 'Saved as draft  Successfully',
              color: 'primary200',
            });

            if (this.newsAndEvents.thumbnail) {
              const thumbnailPayload = new FormData();
              const file = this.newsAndEvents.thumbnail;

              thumbnailPayload.append('file', file);
              thumbnailPayload.append('name', file.name);
              thumbnailPayload.append('type', file.type);
              thumbnailPayload.append('size', file.size);

              this.axios
                .put(`/api/v1/news-and-events/${id}/thumbnail`, thumbnailPayload, {
                  headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then(thumbResponse => {
                  console.log('Thumbnail Uploaded Successfully:', thumbResponse.data);
                })
                .catch(thumbError => {
                  console.error('Error Uploading Thumbnail:', thumbError);
                });
            }

            if (this.files.length > 0) {
              console.log('Uploading files:', this.files);

              const uploadPromises = this.files.map(file => {
                const formData = new FormData();
                formData.append('type', file.type);
                formData.append('name', file.name);
                formData.append('size', file.size);
                formData.append('file', file.file);

                return this.axios
                  .put(`/api/v1/news-and-events/${id}/file`, formData)
                  .then(response => {
                    console.log(`File ${file.name} uploaded successfully:`, response.data);
                    return response.data;
                  })
                  .catch(error => {
                    console.error(`Error uploading file ${file.name}:`, error);
                    EventBus.$emit('snackbar', {
                      text: `Failed to upload ${file.name}. Please try again.`,
                      color: 'error',
                    });
                  });
              });

              Promise.all(uploadPromises).then(() => {
                console.log('All files uploaded.');
                EventBus.$emit('snackbar', {
                  text: 'Saved as draft Successfully',
                  color: 'primary200',
                });

                this.files = [];
              });
            }

            this.$router.push({ name: 'NewsAndEvents' });
          })
          .catch(error => {
            console.error('Error:', error);
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      }
    },
    publish() {
      const isLeftSideFormValid = this.$refs.lestSideForm.validate();
      const isMainSideFormValid = this.$refs.mainBodyForm.validate();

      if (!this.newsAndEvents.description) {
        EventBus.$emit('snackbar', {
          text: 'Description is required to proceed with publish.',
          color: 'error',
        });
        return;
      }

      let dateFrom = null;
      let dateTo = null;
      let date = null;

      if (this.newsAndEvents.eventDate && this.newsAndEvents.eventDate.length > 0) {
        if (this.newsAndEvents.eventDate.length === 1) {
          date = this.newsAndEvents.eventDate[0];
          dateFrom = '';
          dateTo = '';
        } else if (this.newsAndEvents.eventDate.length === 2) {
          dateFrom = this.newsAndEvents.eventDate[0];
          dateTo = this.newsAndEvents.eventDate[1];
          date = '';
        }
      }

      if (isLeftSideFormValid && isMainSideFormValid) {
        EventBus.$emit('waiting', true);
        const payload = {
          title: this.newsAndEvents.title,
          description: this.newsAndEvents.description,
          tags: this.tags,
          type: this.newsAndEvents.type,
          status: 'PUBLISHED',
          scientificsIds: this.scientificsIds,
          ...(this.editNewsAndEvents && { id: this.$route.params.id }),

          ...(this.newsAndEvents.type === 'NEWS' && { date: this.newsAndEvents.date }),

          ...(this.newsAndEvents.type === 'EVENT' &&
            (date ? { date } : { ...(dateFrom && { dateFrom }), ...(dateTo && { dateTo }) })),
        };

        console.log('Publishing payload:', payload);

        this.axios
          .post(`/api/v1/news-and-events`, payload)
          .then(response => {
            console.log('Published Successfully:', response.data);
            const id = response.data.id;

            EventBus.$emit('snackbar', {
              text: 'Published Successfully',
              color: 'primary200',
            });

            if (this.newsAndEvents.thumbnail) {
              const thumbnailPayload = new FormData();
              const file = this.newsAndEvents.thumbnail;

              thumbnailPayload.append('file', file);
              thumbnailPayload.append('name', file.name);
              thumbnailPayload.append('type', file.type);
              thumbnailPayload.append('size', file.size);

              this.axios
                .put(`/api/v1/news-and-events/${id}/thumbnail`, thumbnailPayload, {
                  headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then(thumbResponse => {
                  console.log('Thumbnail Uploaded Successfully:', thumbResponse.data);
                })
                .catch(thumbError => {
                  console.error('Error Uploading Thumbnail:', thumbError);
                });
            }

            if (this.files.length > 0) {
              console.log('Uploading files:', this.files);

              const uploadPromises = this.files.map(file => {
                const formData = new FormData();
                formData.append('type', file.type);
                formData.append('name', file.name);
                formData.append('size', file.size);
                formData.append('file', file.file);

                return this.axios
                  .put(`/api/v1/news-and-events/${id}/file`, formData)
                  .then(response => {
                    console.log(`File ${file.name} uploaded successfully:`, response.data);
                    return response.data;
                  })
                  .catch(error => {
                    console.error(`Error uploading file ${file.name}:`, error);
                    EventBus.$emit('snackbar', {
                      text: `Failed to upload ${file.name}. Please try again.`,
                      color: 'error',
                    });
                  });
              });

              Promise.all(uploadPromises).then(() => {
                console.log('All files uploaded.');
                EventBus.$emit('snackbar', {
                  text: 'Published Successfully',
                  color: 'primary200',
                });

                this.files = [];
              });
            }

            this.$router.push({ name: 'NewsAndEvents' });
          })
          .catch(error => {
            console.error('Error:', error);
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      }
    },
    getNewsAndEvents() {
      const id = this.$route.params.id;

      if (id) {
        let thumbnailName = '';

        this.axios
          .get(`api/v1/news-and-events/${id}`)
          .then(response => {
            console.log('News and Events Response Data:', response.data);

            this.newsAndEvents.type = response.data.type;
            this.newsAndEvents.title = response.data.title;
            this.newsAndEvents.description = response.data.description;
            this.tags = response.data.tags;
            this.newsAndEvents.date = response.data.date;
            this.newsAndEvents.eventDate = response.data.date
              ? [response.data.date]
              : response.data.dateFrom && response.data.dateTo
              ? [response.data.dateFrom, response.data.dateTo]
              : this.newsAndEvents.eventDate;

            if (response.data.files && response.data.files.length > 0) {
              this.filesFromResponse = response.data.files;
            }

            if (response.data.scientifics && response.data.scientifics.length) {
              this.newsAndEvents.domains = [
                ...new Set(response.data.scientifics.map(scientific => scientific.domain)),
              ];

              this.newsAndEvents.subDomains = response.data.scientifics
                .filter(scientific => this.newsAndEvents.domains.includes(scientific.domain))
                .map(scientific => scientific.id);
            }

            if (response.data.thumbnail) {
              thumbnailName = response.data.thumbnail;

              return this.axios.get(`/api/v1/news-and-events/${id}/thumbnail/${thumbnailName}`, {
                responseType: 'blob',
              });
            }
          })
          .then(thumbnailResponse => {
            if (thumbnailResponse) {
              const blob = thumbnailResponse.data;

              console.log('Thumbnail Data:', blob);

              const thumbnailFile = new File([blob], thumbnailName, { type: blob.type });

              this.newsAndEvents.thumbnail = thumbnailFile;

              this.dialogThumbnail = URL.createObjectURL(blob);
            }
          })
          .catch(error => {
            console.error('Error fetching:', error);
          });
      }
    },
    togglePreview() {
      this.previewDialog = true;
      this.dialogTitle = this.newsAndEvents.title ? this.newsAndEvents.title : 'Title';
      this.dialogDate = this.newsAndEvents.date ? this.newsAndEvents.date : null;
      this.dialogDescription = this.newsAndEvents.description;
      this.dialogType = this.newsAndEvents.type;
    },
    addFile() {
      if (this.selectedFile) {
        this.files.push({
          name: this.selectedFile.name,
          size: this.selectedFile.size,
          type: this.selectedFile.type,
          file: this.selectedFile,
        });
        this.selectedFile = null;
      }
    },
    removeFile(index) {
      this.files.splice(index, 1);
    },
    removefileFromResponse(index) {
      const id = this.$route.params.id;
      const fileToRemove = this.filesFromResponse[index];

      if (fileToRemove) {
        this.filesFromResponse.splice(index, 1);

        console.log('File to remove:', fileToRemove);

        this.axios
          .put(`api/v1/news-and-events/${id}/file?filename=${encodeURIComponent(fileToRemove)}`)
          .then(response => {
            console.log('Response Remove File:', response.data);
            EventBus.$emit('snackbar', {
              text: 'File deleted successfully!',
              color: 'primary200',
            });
          })
          .catch(error => {
            console.log('Error:', error);
            EventBus.$emit('snackbar', {
              text: 'Failed to delete file. Please try again.',
            });
          });
      } else {
        console.log('Invalid file or id');
      }
    },
    cancel() {
      this.$router.push({ name: 'NewsAndEvents' });
    },
    addTag() {
      const tag = this.newsAndEvents.tag.trim();
      if (tag && !this.tags.includes(tag)) {
        this.tags.push(tag);
      }
      this.newsAndEvents.tag = '';
    },
    removeTag(index) {
      this.tags.splice(index, 1);
    },
    validateDateRange() {
      const [startDate, endDate] = this.newsAndEvents.eventDate;

      if (startDate && endDate && startDate > endDate) {
        this.newsAndEvents.eventDate = [endDate, startDate];
      }
    },
    formatDate(dateStr) {
      const date = new Date(dateStr);
      const options = { day: '2-digit', month: 'short', year: 'numeric' };
      return date.toLocaleDateString('en-GB', options);
    },
  },
};
</script>

<style>
.custom-button {
  color: black;
  font-weight: bold;
  margin: 0 2px;
}

.custom-button:hover {
  background: rgba(157, 87, 244, 1);
  color: white;
  font-weight: bold;
}
.tiptap-vuetify-editor__content {
  min-height: 200px;
}

.tiptap-vuetify-editor {
  border: 1px solid #c4c4c4 !important;
}

.ProseMirror {
  min-height: 200px;
}
</style>
